// Présence en ligne — heartbeat + abonnement temps réel.
//
// Chaque client envoie un signal de vie (heartbeat) toutes les 45 s au backend,
// avec son activité en cours (fiche consultée / en édition). Le backend upsert
// la ligne UserPresence de l'utilisateur et la publie via Mercure : tous les
// panneaux « Équipe » et badges de fiche se mettent à jour en direct.
//
// L'état « hors ligne » n'est jamais stocké : il est déduit quand le dernier
// heartbeat date de plus de STALE_MS (navigateur fermé, crash, veille…).

import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import authService from '../services/auth.service';
import { useMercure } from './mercure';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const HEARTBEAT_MS = 45000;
/**
 * Refetch léger périodique des présences : le backend ne diffuse plus les
 * keep-alive purs via Mercure (optimisation de charge à ~170 comptes), donc
 * les lastSeenAt sont resynchronisés par ce polling doux.
 */
const REFETCH_MS = 90000;
/**
 * Au-delà de ce délai sans signe de vie, l'utilisateur est considéré hors ligne.
 * Doit rester > heartbeat (45 s) + refetch (90 s) pour éviter les faux « hors ligne ».
 */
export const STALE_MS = 3 * 60 * 1000;
/** Les événements Mercure de présence sont appliqués par paquets (limite les re-rendus) */
const APPLY_BATCH_MS = 2000;

// Activité en cours, partagée par toute l'appli, sur deux niveaux :
//  - pageActivity  : la page où je suis (« le Planning », « les Projets »…)
//  - focusActivity : la fiche ouverte / en édition (prioritaire sur la page)
let pageActivity = null;
let focusActivity = null;
let sendTimer = null;

function effectiveActivity() {
    return focusActivity || pageActivity;
}

// ── Coordination multi-onglets (via localStorage, partagé même origine) ─────
// Sans ça, deux onglets SPOT ouverts font clignoter l'état : l'onglet visible
// envoie « actif », l'onglet caché « inactif », chacun sur sa propre horloge
// → les collègues voient l'utilisateur alterner En ligne / Inactif en boucle.
// Et fermer UN onglet envoyait « déconnecté » alors que l'autre restait ouvert.

const TAB_ID = Math.random().toString(36).slice(2);
const VIS_KEY = 'spot-presence-visible-at'; // dernier instant où UN onglet était visible
const TABS_KEY = 'spot-presence-tabs';      // { tabId: dernier battement } des onglets vivants

function marquerVivant() {
    try {
        const now = Date.now();
        const map = JSON.parse(localStorage.getItem(TABS_KEY) || '{}');
        map[TAB_ID] = now;
        // Purge des onglets morts (crash, fermeture sans pagehide…)
        Object.keys(map).forEach((k) => {
            if (now - map[k] > 2 * HEARTBEAT_MS + 30000) delete map[k];
        });
        localStorage.setItem(TABS_KEY, JSON.stringify(map));
        if (document.visibilityState !== 'hidden') {
            localStorage.setItem(VIS_KEY, String(now));
        }
    } catch { /* stockage indisponible : comportement mono-onglet */ }
}

function retirerOnglet() {
    try {
        const map = JSON.parse(localStorage.getItem(TABS_KEY) || '{}');
        delete map[TAB_ID];
        localStorage.setItem(TABS_KEY, JSON.stringify(map));
    } catch { /* ignore */ }
}

function autreOngletVivant() {
    try {
        const now = Date.now();
        const map = JSON.parse(localStorage.getItem(TABS_KEY) || '{}');
        return Object.entries(map).some(([k, ts]) => k !== TAB_ID && now - ts < HEARTBEAT_MS + 20000);
    } catch {
        return false;
    }
}

function buildPayload(etat) {
    const user = authService.getCurrentUser();
    if (!user?.id) return null;
    const activity = effectiveActivity();
    return {
        userId: user.id,
        etat,
        activiteType: activity?.type ?? null,
        activiteId: activity?.id ?? null,
        activiteLabel: activity?.label ?? null,
        enEdition: Boolean(activity?.edition),
    };
}

function currentEtat() {
    if (typeof document === 'undefined' || document.visibilityState !== 'hidden') return 'actif';
    // Onglet caché : si un onglet SPOT (celui-ci ou un autre) était visible il
    // y a peu, l'utilisateur est bel et bien actif → pas de faux « inactif ».
    try {
        const t = Number(localStorage.getItem(VIS_KEY) || 0);
        if (Date.now() - t < HEARTBEAT_MS + 20000) return 'actif';
    } catch { /* ignore */ }
    return 'inactif';
}

function sendHeartbeat(etat = currentEtat()) {
    const body = buildPayload(etat);
    if (!body) return;
    marquerVivant();
    axios.post(`${API}/presence/heartbeat`, body, { withCredentials: true }).catch(() => {});
}

// Envoi débouncé (800 ms) pour absorber les ouvertures/fermetures rapides
function scheduleSend() {
    if (sendTimer) clearTimeout(sendTimer);
    sendTimer = setTimeout(() => {
        sendTimer = null;
        sendHeartbeat();
    }, 800);
}

/**
 * Signale « déconnecté » immédiatement — à appeler AVANT d'effacer la session
 * locale (bouton Déconnexion), sinon l'utilisateur reste affiché « en ligne »
 * jusqu'à l'expiration du délai de 3 min. sendBeacon garantit l'envoi même si
 * la page est quittée juste après.
 */
export function signalPresenceLogout() {
    const body = buildPayload('deconnecte');
    if (!body) return;
    if (navigator.sendBeacon) {
        navigator.sendBeacon(
            `${API}/presence/heartbeat`,
            new Blob([JSON.stringify(body)], { type: 'text/plain' }),
        );
    } else {
        sendHeartbeat('deconnecte');
    }
}

/** Signale la fiche ouverte ({ type, id, label, edition }) ou null pour effacer. */
export function setPresenceActivity(activity) {
    focusActivity = activity;
    scheduleSend();
}

/** Signale la page courante ({ type: 'page', label }) ou null pour effacer. */
export function setPresencePage(activity) {
    pageActivity = activity;
    scheduleSend();
}

/**
 * Hook fiches : signale « je consulte / modifie cette ressource » tant que le
 * composant est monté et que l'activité ne change pas. Prioritaire sur la page.
 * Ex: usePresenceActivity(projet && { type: 'projet', id: projet.id, label: `le projet ${projet.nom}`, edition: true })
 */
export function usePresenceActivity(activity) {
    const key = activity
        ? `${activity.type}|${activity.id}|${activity.edition ? 1 : 0}|${activity.label || ''}`
        : '';
    useEffect(() => {
        if (!key) return undefined;
        setPresenceActivity(activity);
        return () => setPresenceActivity(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);
}

/**
 * Hook pages : signale « je suis sur cette page » (affiché « Sur le Planning »).
 * Ex: usePresencePage('le Planning')
 */
export function usePresencePage(label) {
    useEffect(() => {
        if (!label) return undefined;
        setPresencePage({ type: 'page', id: null, label, edition: false });
        return () => setPresencePage(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [label]);
}

/**
 * Boucle de heartbeat — à monter UNE seule fois (composant TeamPresence).
 * Gère aussi le passage actif/inactif (onglet en arrière-plan) et le signal
 * de déconnexion à la fermeture de l'onglet (sendBeacon, requête garantie).
 */
export function usePresenceHeartbeat() {
    useEffect(() => {
        if (!authService.getCurrentUser()?.id) return undefined;

        sendHeartbeat('actif');
        const interval = setInterval(() => sendHeartbeat(), HEARTBEAT_MS);

        const onVisibility = () => sendHeartbeat();
        const onPageHide = () => {
            retirerOnglet();
            // Un autre onglet SPOT est encore ouvert : lui continue d'émettre,
            // ne pas écraser l'état avec un faux « déconnecté ».
            if (autreOngletVivant()) return;
            const body = buildPayload('deconnecte');
            if (body && navigator.sendBeacon) {
                // text/plain : évite le preflight CORS (le backend lit le corps brut)
                navigator.sendBeacon(
                    `${API}/presence/heartbeat`,
                    new Blob([JSON.stringify(body)], { type: 'text/plain' }),
                );
            }
        };

        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('pagehide', onPageHide);
        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('pagehide', onPageHide);
        };
    }, []);
}

/**
 * Liste des présences, tenue à jour en temps réel.
 *  - Chargement initial + refetch léger toutes les 90 s (resynchronise les
 *    lastSeenAt, car les keep-alive purs ne sont plus diffusés via Mercure).
 *  - Le refetch est suspendu quand l'onglet est en arrière-plan.
 *  - Les événements Mercure (vrais changements d'activité) sont appliqués par
 *    paquets de 2 s pour limiter les re-rendus quand beaucoup de monde navigue.
 * Retourne les lignes brutes de l'API ; utiliser presenceEtat() pour l'état effectif.
 */
export function usePresences() {
    const [presences, setPresences] = useState([]);

    useEffect(() => {
        let cancelled = false;
        const load = () => {
            axios.get(`${API}/user_presences`, {
                withCredentials: true,
                headers: { Accept: 'application/ld+json' },
                params: { pagination: false },
            })
                .then((res) => {
                    if (!cancelled) setPresences(res.data['hydra:member'] || res.data.member || []);
                })
                .catch(() => {});
        };

        load();
        const iv = setInterval(() => {
            if (document.visibilityState !== 'hidden') load();
        }, REFETCH_MS);
        const onVisible = () => {
            if (document.visibilityState === 'visible') load();
        };
        document.addEventListener('visibilitychange', onVisible);

        return () => {
            cancelled = true;
            clearInterval(iv);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, []);

    // Buffer des événements Mercure, appliqués au plus une fois toutes les 2 s
    const bufferRef = useRef(new Map());
    const flushTimerRef = useRef(null);
    useMercure(['user_presences'], (resource, payload, { deleted, id }) => {
        bufferRef.current.set(id, { deleted, payload });
        if (flushTimerRef.current) return;
        flushTimerRef.current = setTimeout(() => {
            flushTimerRef.current = null;
            const updates = bufferRef.current;
            bufferRef.current = new Map();
            setPresences((prev) => {
                const byId = new Map(prev.map((p) => [p.id, p]));
                updates.forEach(({ deleted, payload }, uid) => {
                    if (deleted) byId.delete(uid);
                    else byId.set(uid, payload);
                });
                return Array.from(byId.values());
            });
        }, APPLY_BATCH_MS);
    });
    useEffect(() => () => {
        if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    }, []);

    return presences;
}

/** État effectif d'une ligne de présence : 'actif' | 'inactif' | 'hors_ligne'. */
export function presenceEtat(p, now = Date.now()) {
    if (!p) return 'hors_ligne';
    const seen = p.lastSeenAt ? new Date(p.lastSeenAt).getTime() : 0;
    if (!seen || now - seen > STALE_MS || p.etat === 'deconnecte') return 'hors_ligne';
    return p.etat === 'inactif' ? 'inactif' : 'actif';
}

/**
 * Présences des COLLÈGUES sur un type de ressource, groupées par id :
 *   const presenceByProjet = usePresenceOn('projet');
 *   presenceByProjet[123] → [{ utilisateurPrenom, enEdition, … }]
 * Exclut l'utilisateur courant et les présences périmées ; se met à jour en
 * temps réel (Mercure) et fait « vieillir » les états toutes les 30 s.
 */
export function usePresenceOn(type) {
    const presences = usePresences();
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        const iv = setInterval(() => setNow(Date.now()), 30000);
        return () => clearInterval(iv);
    }, []);

    return useMemo(() => {
        const meId = Number(authService.getCurrentUser()?.id);
        const map = {};
        presences.forEach((p) => {
            if (p.activiteType !== type || p.activiteId == null) return;
            if (Number(p.utilisateurId) === meId) return;
            if (presenceEtat(p, now) === 'hors_ligne') return;
            (map[p.activiteId] = map[p.activiteId] || []).push(p);
        });
        return map;
    }, [presences, now, type]);
}
